import React, { useMemo } from "react";
import clsx from "clsx";
import { FormProvider, useForm } from "react-hook-form";
import { OptionsOrGroups, GroupBase } from "react-select";

import {
	Button,
	HasPermissions,
	HookFormCheckbox,
	HookFormInputGroup,
	HookSelectFormGroup,
} from "components";
import { permissionList } from "helpers";
import { DealNote } from "types";
import { trimMidSentenceNormalizer, trimNormalizer } from "validators";

import { NoteDiffList } from "./noteDiffList";
import validators from "./validators";

import styles from "./notes.module.scss";

type NoteFormValues = {
	note: string;
	title: string;
	phase?: string;
	id?: string;
	category?: number;
};

type NoteFormProps = {
	dealNote: DealNote & {
		privateEmployeesOnly?: boolean;
		privateMangersOnly?: boolean;
		privateRiskUwOnly?: boolean;
	};
	brandingColor: boolean;
	loading: boolean;
	showDiff: boolean;
	handleSave: ({ note, title, phase, id }: NoteFormValues) => void;
	isHideViewPrivatesNotes: boolean;
	dealNotesCategories:
		| OptionsOrGroups<{ [key: string]: any }, GroupBase<any>>
		| undefined;
};

export const NoteForm = ({
	dealNote,
	handleSave,
	showDiff,
	brandingColor,
	loading,
	isHideViewPrivatesNotes,
	dealNotesCategories,
}: NoteFormProps) => {
	const formMethods = useForm({
		mode: "all",
		reValidateMode: "onChange",
		defaultValues: dealNote,
	});

	const {
		register,
		handleSubmit: onSubmit,
		formState: { errors, isDirty, defaultValues },
		watch,
	} = formMethods;

	const handleSubmit = ({
		note,
		title,
		phase,
		category,
		...values
	}: DealNote) => {
		handleSave({
			...values,
			note: trimMidSentenceNormalizer(note),
			title,
			phase: phase?.value?.toString(),
			id: dealNote?.id,
			category: category?.value,
		});
	};

	const noteChanges = useMemo(() => {
		if (showDiff) {
			return dealNote?.noteChanges?.map(noteChanges => {
				return {
					author: dealNote?.author,
					createdAt: noteChanges.createdAt,
					title: noteChanges.newTitle,
					category: dealNote?.category,
					note: noteChanges.newNote,
					id: noteChanges.id,
					isPrivate: dealNote.isPrivate,
					lastNoteChange: {
						oldNote: noteChanges.oldNote,
						oldTitle: noteChanges.oldTitle,
					},
					noteChanges: [
						{
							createdAt: noteChanges.createdAt,
							id: noteChanges.id,
							newNote: noteChanges.newNote,
							newTitle: noteChanges.newTitle,
							oldNote: noteChanges.oldNote,
							oldTitle: noteChanges.oldTitle,
						},
					],
				};
			});
		}
		return [];
	}, [dealNote, showDiff]);

	const privateEmployeesOnlyValue = watch(
		"privateEmployeesOnly",
		defaultValues?.privateEmployeesOnly,
	);
	const privateMangersOnlyValue = watch(
		"privateMangersOnly",
		defaultValues?.privateMangersOnly,
	);
	const privateRiskUwOnlyValue = watch(
		"privateRiskUwOnly",
		defaultValues?.privateRiskUwOnly,
	);

	return (
		<FormProvider {...formMethods}>
			<form onSubmit={onSubmit(handleSubmit)}>
				<HookFormInputGroup
					name="title"
					register={register}
					label="Title"
					colInput={12}
					colLabel={12}
					autoComplete="off"
					validator={validators.title}
					normalize={trimMidSentenceNormalizer}
					showRequiredMark
					normalizeOnBlur={trimNormalizer}
					errors={errors}
					valueClassName={brandingColor ? "brandingColor" : ""}
				/>
				{dealNotesCategories && (
					<HookSelectFormGroup
						name="category"
						label="Category"
						options={dealNotesCategories}
						getOptionLabel={option => option.displayName}
						getOptionValue={option => option.value}
						colLabel={{ sm: 12 }}
						colSelect={{ sm: 12 }}
						brandingColor={brandingColor}
						menuPlacement="bottom"
						showRequiredMark
						rules={{
							validate: validators.category,
						}}
						defaultOptions
					/>
				)}
				<HookFormInputGroup
					name="note"
					register={register}
					label="Body"
					colInput={12}
					colLabel={12}
					autoComplete="off"
					validator={validators.note}
					showRequiredMark
					errors={errors}
					as="textarea"
					valueClassName={clsx(
						styles.noteBody,
						brandingColor ? "brandingColor" : "",
					)}
				/>
				{!isHideViewPrivatesNotes && (
					<>
						<HasPermissions
							requiredPermissions={
								permissionList.ViewPrivateNotesEmployeesOnly
							}
						>
							<HookFormCheckbox
								register={register}
								id="privateEmployeesOnly"
								label="Private Note. Employees Only"
								name="privateEmployeesOnly"
								className="mb-4"
								disabled={
									!privateEmployeesOnlyValue &&
									(privateMangersOnlyValue ||
										privateRiskUwOnlyValue)
								}
							/>
						</HasPermissions>
						<HasPermissions
							requiredPermissions={
								permissionList.ViewPrivateNotesManagersOnly
							}
						>
							<HookFormCheckbox
								register={register}
								id="privateMangersOnly"
								label="Private Note. Managers Only"
								name="privateMangersOnly"
								className="mb-4"
								disabled={
									!privateMangersOnlyValue &&
									(privateEmployeesOnlyValue ||
										privateRiskUwOnlyValue)
								}
							/>
						</HasPermissions>
						<HasPermissions
							requiredPermissions={
								permissionList.ViewPrivateNotesUwOnly
							}
						>
							<HookFormCheckbox
								register={register}
								id="privateRiskUwOnly"
								label="Private Note. Risk / UW Only"
								name="privateRiskUwOnly"
								disabled={
									!privateRiskUwOnlyValue &&
									(privateEmployeesOnlyValue ||
										privateMangersOnlyValue)
								}
							/>
						</HasPermissions>
					</>
				)}
				<div className="text-center py-4">
					<Button type="submit" value="Save" disabled={!isDirty || loading} isLoading={loading} />
				</div>
				{showDiff && (
					<NoteDiffList
						notes={noteChanges}
						loading={loading}
						className="mt-4"
					/>
				)}
			</form>
		</FormProvider>
	);
};
